import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BillingService } from '@galaxy/billing';
import { Addon } from '@galaxy/marketplace-apps/v1';
import { BilledProduct, buildBilledProductFromProductPricingAndAddon } from '@vendasta/shared';
import { Subscription } from 'rxjs';
import { Pricing } from '../pricing/pricing';

@Component({
  selector: 'store-va-addon-list',
  templateUrl: './addon-list.component.html',
  styleUrls: ['./addon-list.component.scss'],
})
export class VaAddonListComponent implements OnDestroy, OnInit {
  _addons: Addon[];
  @Input()
  set addons(addons: Addon[]) {
    this._addons = addons;
    this.populateBilledProduct();
  }
  @Input() showPricing = true;
  @Input() partnerID: string;

  _hasVerifiedContract: boolean = null;
  @Input()
  set hasVerifiedContract(value: boolean) {
    this._hasVerifiedContract = value;
    this.populateBilledProduct();
  }
  _canAccessContractPricing: boolean = null;
  @Input()
  set canAccessContractPricing(value: boolean) {
    this._canAccessContractPricing = value;
    this.populateBilledProduct();
  }

  @Output() addonSelected = new EventEmitter<string>();

  billedProductsMap: Map<string, BilledProduct> = new Map();
  private billedProductLoaded: Map<string, boolean> = new Map();
  private subscriptions: Subscription[] = [];

  constructor(
    private billingService: BillingService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.populateBilledProduct();
  }

  populateBilledProduct(): void {
    if (this.displayContractPricing() && this.partnerID) {
      const billingIDs = this._addons.map((a) => {
        this.billedProductsMap.set(a.addonId, null);
        this.billedProductLoaded.set(a.addonId, false);
        return a.billingId;
      });
      this.subscriptions.push(
        this.billingService.getMultiProductPricing(this.partnerID, billingIDs).subscribe((pricing) => {
          this._addons.forEach((addon) => {
            const addonPricing = pricing[addon.billingId];
            if (!addonPricing) {
              return;
            }
            const billedProduct = buildBilledProductFromProductPricingAndAddon(addon, addonPricing);
            this.billedProductsMap.set(addon.addonId, billedProduct);
            this.billedProductLoaded.set(addon.addonId, true);
          });
          this.cdr.detectChanges();
        }),
      );
    }
  }

  onClick(addonId: string): void {
    this.addonSelected.emit(addonId);
  }

  public instanceOfPricing(object: any): boolean {
    if (typeof object === 'number') {
      return false;
    }
    return object.currency !== undefined && object.prices !== undefined;
  }

  getDisplayPricing(addon: Addon): Pricing {
    const price: any = addon.price;
    if (price && this.instanceOfPricing(price)) {
      return price;
    }
    return {
      currency: addon.currency,
      prices: [
        {
          price: addon.price,
          frequency: addon.billingFrequency ? addon.billingFrequency : 'Monthly',
        },
      ],
    };
  }

  noData(): boolean {
    return (
      this._hasVerifiedContract === undefined ||
      this._hasVerifiedContract === null ||
      this._canAccessContractPricing === undefined ||
      this._canAccessContractPricing === null
    );
  }

  displayContractPricing(): boolean {
    return this._canAccessContractPricing && this._hasVerifiedContract;
  }

  isBilledProductLoaded(addon: Addon): boolean {
    if (this.noData()) {
      return false;
    }
    if (this.displayContractPricing()) {
      const value = this.billedProductLoaded.get(addon.addonId);
      return value ? value : false;
    }
    return true;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
